/**
 * 69.59, the helicopter (DECISIONS 270 (4), the owner: "Then I also probably want to introduce the helicopter."; 63.13's
 * loiterer beside the dropship, the Oct 19 concepts: "To sort of justify having a helicopter and a dropship which can both
 * loiter effectively"): four looks to the D bar (rendering/vehicleLooks.ts' profiles extruded and chamfered, separate moving
 * parts), each for a purpose —
 *   - **A, the surplus utility** — the concepts sheet's (c) rotorcraft as it stands (aircraftConcepts.ts `surplus`): an old
 *     two-blade utility ship, a bench of four, door gunners' .50s, rocket pods on stub pylons, a light chin gun, skids. Light
 *     transport first, fire second; cheap, lost without tears.
 *   - **B, the attack ship** — a narrow tandem two-seater (the gunner low in front, the pilot above behind), stub wings with a
 *     rocket pod and a rail of four guided missiles a side, a chin cannon in a turret, the exhaust turned up. Loitering fire
 *     only: it carries no one.
 *   - **C, the armed utility** — the expeditionary force's good gear: a wide cabin of eight, twin engines with boxed IR
 *     suppressors, a four-blade rotor, sliding doors with a .50 in each, stub wings over the cabin with a pod a side, fixed
 *     wheels, flare dispensers on the boom. Both: transport with real fire.
 *   - **D, the compound** — coaxial rotors (no tail rotor) and a pusher propeller: faster in the cruise, a cabin of six, a
 *     chin gun; the newest and the dearest.
 * Each carries its seats and gun mounts as data (`heliData`, person heights, its frame on its gear) and the places its
 * flares leave (`dispensers`) and its exhaust shows (`exhausts`, the IR seeker's point). Person units (a person is 1.8), +z
 * forward. Presentation only (design_sheets/helicopter.html); not in play — the sim is 69.59's build, after the picks.
 */
import * as THREE from 'three';
import { bakeStaticParts } from './meshBake';
import type { UnitPalette } from './unitPalette';
import { box, group, anchor, mat } from './vehicleModels';
import { slab, top, front, ring, rect, rev, cyl } from './vehicleLooks';
import { buildPlayHmg } from './gunLooks';
import { buildAircraft, type AircraftModel, type AircraftRole, type AircraftGun } from './aircraftConcepts';

/** (the parts' builders and colours are exported for 73.1's style sheet, design_sheets/helicopter_style.html) */
export const STEEL = '#5e615e', RUBBER = '#1d1e1f', GLASS = '#1c2328', SHIELD = '#2b2927';

export type HeliLook = 'utility' | 'attack' | 'armed' | 'compound';
export const HELI_LOOKS: readonly HeliLook[] = ['utility', 'attack', 'armed', 'compound'];

/** Each look's line for the sheet: its purpose, what it carries, its crew, how it flies, and what it asks of the sim. */
export const HELI_NOTES: Record<HeliLook, { letter: string; name: string; purpose: string; weapons: string; crew: string; flight: string; sim: string }> = {
  utility: {
    letter: 'A', name: 'A · The surplus utility (the concepts sheet\'s rotorcraft)',
    purpose: 'Light transport first, fire second: four in the cabin, two door guns to cover its own landing, a few rockets to clear the way in. Old, simple, cheap to keep, lost without tears — the force\'s dated surplus.',
    weapons: 'a .50 in each doorway (the door gunners), a rocket pod on each stub pylon (7 rockets: the LAW\'s burst, unguided), a light chin gun',
    crew: 'two pilots, two door gunners; four riders on the bench',
    flight: 'a two-blade teeter rotor and a tail rotor: hovers anywhere, cruise ~30 people/s, top ~40; turns by banking ~0.5 rad/s at cruise, pedal-turns 1 rad/s in the hover',
    sim: 'a third craft kind in the one flight system (one lifter that tilts both ways: the disc), the door guns as mounts on the seat path (64.11), the pods as the rocket mount (69.21)',
  },
  attack: {
    letter: 'B', name: 'B · The attack ship',
    purpose: 'Loitering fire only: it circles a called point low and holds it — the gunship\'s job in a smaller, cheaper, harder-to-see ship. It carries no one.',
    weapons: 'a 20 mm rotary cannon in a chin turret (it turns and pitches), a rocket pod and a rail of four guided missiles under each stub wing (the anti-armour round: 65.49\'s homing core at a ground target)',
    crew: 'two in tandem: the gunner low in front, the pilot above and behind',
    flight: 'a four-blade rotor and a tail rotor; the narrowest ship to see head-on; cruise ~35 people/s, top ~48; the most agile of the four (~0.8 rad/s at cruise)',
    sim: 'the chin cannon as a mounted gun (MOUNTED_GUNS), its missiles on the homing core at a point (the gunship\'s guided rockets\' path), a loiter orbit for a bot pilot (the gunship\'s, smaller)',
  },
  armed: {
    letter: 'C', name: 'C · The armed utility',
    purpose: 'Both: a squad in and out under its own fire. The good-gear ship — twin engines, IR suppressors, flares — the hot rescue\'s ship that goes in.',
    weapons: 'a .50 in each sliding door, a rocket pod on each stub wing over the cabin (the pods\' rockets), flare dispensers on the boom',
    crew: 'two pilots, two door gunners; eight riders (a squad of 3 plus a squad of 5, or a squad and its wounded)',
    flight: 'a four-blade rotor, twin engines (one lost: it flies on, slower — 69.3b\'s nacelle rule), cruise ~33 people/s, top ~45, ~0.6 rad/s at cruise',
    sim: 'as A, with the second engine as a part (one lost lowers the climb and the top speed), the IR suppressors as a lower seeker reach (a dial on the IR missile\'s lock)',
  },
  compound: {
    letter: 'D', name: 'D · The compound',
    purpose: 'Fast transport: coaxial rotors and a pusher propeller — it cruises near the dropship\'s 40 and hovers like a helicopter. The newest and the dearest; six in the cabin.',
    weapons: 'a 20 mm chin gun, a .50 in the cabin door (one side), two rocket pods on fold-out arms',
    crew: 'two pilots, a door gunner; six riders',
    flight: 'two coaxial four-blade rotors (no tail rotor: it yaws by the rotors\' difference), a pusher propeller for the cruise; cruise ~40 people/s, top ~55, ~0.6 rad/s',
    sim: 'as A, with the pusher as a second thrust (its speed a dial); no tail rotor to shoot off',
  },
};

export interface HeliModel {
  look: HeliLook;
  root: THREE.Group;
  /** rides the flight's bank and pitch */
  body: THREE.Group;
  /** turned about y by the aim, about x by the aim's pitch */
  yaw: THREE.Group[];
  pitch: THREE.Group[];
  /** the rotors and the props (spun by thrust) */
  spin: Array<{ group: THREE.Group; axis: 'x' | 'y' | 'z'; rate: number }>;
  /** sliding doors (`slide` × share along z) */
  slides: Array<{ group: THREE.Group; home: number; slide: number }>;
  seats: Array<{ role: AircraftRole; at: THREE.Object3D; gun?: AircraftGun }>;
  mounts: Array<{ gun: AircraftGun; at: THREE.Object3D }>;
  /** where a flare salvo leaves (each pointed out and down: its -z the throw) */
  dispensers: THREE.Object3D[];
  /** where its exhaust shows (the IR seeker's point; its +z the plume's way) */
  exhausts: THREE.Object3D[];
  /** the main rotor's radius (people / 1.8 units): the sheet's disc and the size row */
  rotorR: number;
}

/** A rotor: the mast, its hub, `n` blades of radius `r` drooping a little (it spins about y), on `parent` at (x, y, z). */
export function rotor(m: HeliModel, parent: THREE.Object3D, x: number, y: number, z: number, n: number, r: number, color: string, rate: number, chord = 0.5): THREE.Group {
  const g = group(parent, x, y, z); m.spin.push({ group: g, axis: 'y', rate });
  cyl(g, 0.3, 0.24, color, 0, 0, 0, 'y', 8);
  for (let i = 0; i < n; i++) {
    const blade = group(g, 0, 0.06, 0); blade.rotation.y = (i * 2 * Math.PI) / n;
    slab(blade, [0.3, -0.03, r, -0.12, r, 0.0, 0.3, 0.04], chord, color, 0, 0);
    box(g, 0.06, 0.25, 0.06, STEEL, Math.cos((i * 2 * Math.PI) / n) * 0.22, -0.15, -Math.sin((i * 2 * Math.PI) / n) * 0.22);   // a pitch link
  }
  return g;
}
/** A tail rotor of `n` blades of radius `r` beside the fin, on its side `side` (it spins about x). */
export function tailRotor(m: HeliModel, parent: THREE.Object3D, x: number, y: number, z: number, n: number, r: number, color: string): void {
  cyl(parent, 0.2, 0.3, color, x * 0.6, y, z, 'x', 8);   // its gearbox
  const g = group(parent, x, y, z); m.spin.push({ group: g, axis: 'x', rate: 60 });
  for (let i = 0; i < n; i++) { const b = box(g, 0.04, r * 2, 0.18, color, 0, 0, 0); b.rotation.x = (i * Math.PI) / n; }
}
/** A rocket pod along z: its tube with seven holes, its fairing nose, its hanger. */
export function rocketPod(parent: THREE.Object3D, p: UnitPalette, x: number, y: number, z: number, len = 1.4, r = 0.28): THREE.Group {
  const holes = [rev(ring(0, 0, r * 0.2, 6))];
  for (let i = 0; i < 6; i++) holes.push(rev(ring(Math.cos(i * Math.PI / 3) * r * 0.58, Math.sin(i * Math.PI / 3) * r * 0.58, r * 0.17, 6)));
  const pod = group(parent, x, y, z);
  front(pod, ring(0, 0, r, 12), len, p.dark, 0, 0.03, holes);
  front(pod, ring(0, 0, r * 1.02, 12), 0.12, p.flash, -len / 2 + 0.2, 0, [rev(ring(0, 0, r * 0.96, 12))]);
  box(pod, r * 0.5, 0.14, len * 0.6, p.metal, 0, r + 0.05, 0);
  return pod;
}
/** A rail of four guided missiles (two over two) along z under a wing, its hanger. */
function missileRail(parent: THREE.Object3D, p: UnitPalette, x: number, y: number, z: number): THREE.Group {
  const g = group(parent, x, y, z);
  box(g, 0.5, 0.08, 1.5, p.metal, 0, 0.18, 0);
  for (const [dx, dy] of [[-0.13, 0], [0.13, 0], [-0.13, -0.26], [0.13, -0.26]]) {
    cyl(g, 0.085, 1.4, '#c9c6b8', dx, dy, 0, 'z', 8);
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.085, 0.24, 8), mat('#c9c6b8')); nose.rotation.x = Math.PI / 2; nose.position.set(dx, dy, 0.82); g.add(nose);
    box(g, 0.3, 0.01, 0.16, p.dark, dx, dy, -0.62);
  }
  return g;
}
/** A flare dispenser: a small block of cells on the skin facing out and down, its anchor at its face. */
export function dispenser(m: HeliModel, parent: THREE.Object3D, x: number, y: number, z: number, side: number): void {
  const d = group(parent, x, y, z);
  box(d, 0.12, 0.3, 0.5, SHIELD, 0, 0, 0);
  for (let i = 0; i < 3; i++) for (let j = 0; j < 2; j++) box(d, 0.02, 0.07, 0.1, '#111214', side * 0.065, -0.07 + j * 0.14, -0.15 + i * 0.15);
  const a = anchor(d, side * 0.08, 0, 0);
  a.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, -1), new THREE.Vector3(side * 5, -3, -2).normalize());   // (its -z: the throw, out, down and back, in the body's frame)
  m.dispensers.push(a);
}
/** An exhaust's anchor at (x, y, z) pointing along `dir` (the plume's way): the IR seeker's point. */
export function exhaust(m: HeliModel, parent: THREE.Object3D, x: number, y: number, z: number, dir: THREE.Vector3): void {
  const a = anchor(parent, x, y, z); a.lookAt(dir.clone().add(a.position)); m.exhausts.push(a);
}
export function blank(look: HeliLook, personHeight: number): HeliModel {
  const root = new THREE.Group(); root.scale.setScalar(personHeight / 1.8);
  return { look, root, body: group(root), yaw: [], pitch: [], spin: [], slides: [], seats: [], mounts: [], dispensers: [], exhausts: [], rotorR: 0 };
}

// ---------------------------------------------------------------------------
// A: the concepts sheet's rotorcraft, as it stands, with its flares and its exhaust marked
// ---------------------------------------------------------------------------

function utility(p: UnitPalette, personHeight: number): HeliModel & { concept: AircraftModel } {
  const a = buildAircraft('surplus', p, personHeight);
  const m: HeliModel & { concept: AircraftModel } = { look: 'utility', root: a.root, body: a.body, yaw: a.yaw, pitch: a.pitch, spin: a.spin, slides: a.slides, seats: a.seats, mounts: a.mounts, dispensers: [], exhausts: [], rotorR: 6.8, concept: a };
  const S = 0.75;
  for (const side of [-1, 1]) dispenser(m, a.body, side * 0.5, S + 1.35, -4.2, side);   // (added: under the boom's roots — the concepts sheet drew none)
  exhaust(m, a.body, 0.55, S + 2.55, -3.2, new THREE.Vector3(0, 0.2, -1));
  return m;
}

// ---------------------------------------------------------------------------
// B: the attack ship
// ---------------------------------------------------------------------------

function attack(p: UnitPalette, personHeight: number): HeliModel {
  const m = blank('attack', personHeight), body = m.body;
  const S = 0.72, W = 0.95;
  // the fuselage: a pointed nose over the chin turret, the stepped tandem canopy, the engine hump, the boom to the fin
  slab(body, [5.4, S + 0.7, 4.7, S + 0.22, 3.3, S, -1.6, S, -2.9, S + 0.72, -8.7, S + 1.2, -8.7, S + 1.55, -3.1, S + 1.6, -1.6, S + 1.78, 0.3, S + 1.62, 1.35, S + 1.28, 2.5, S + 1.16, 3.7, S + 1.0, 4.8, S + 0.92], W, p.body, 0, 0.12).name = 'body';
  slab(body, [1.7, S + 1.62, 1.2, S + 2.15, -2.3, S + 2.25, -3.0, S + 1.95, -3.1, S + 1.6], 1.25, p.dark, 0, 0.14);   // the engine housing
  // the canopies: the gunner's low in front, the pilot's stepped up behind (one frame)
  slab(body, [4.25, S + 1.02, 3.0, S + 1.6, 1.95, S + 1.62, 2.0, S + 1.18, 3.7, S + 1.0], W + 0.04, GLASS, 0, 0.05);
  slab(body, [1.95, S + 1.62, 1.55, S + 2.1, 0.2, S + 2.12, 0.3, S + 1.62], W + 0.04, GLASS, 0, 0.05);
  box(body, W + 0.06, 0.06, 0.08, p.metal, 0, S + 1.62, 1.95);
  // the exhaust turned up and out (the seeker sees less of it from below)
  for (const side of [-1, 1]) {
    const ex = group(body, side * 0.55, S + 2.0, -2.7); ex.rotation.z = side * -0.5;
    cyl(ex, 0.17, 0.5, STEEL, 0, 0.15, 0, 'y', 10); cyl(ex, 0.12, 0.52, RUBBER, 0, 0.16, 0, 'y', 10);
    exhaust(m, body, side * 0.72, S + 2.3, -2.7, new THREE.Vector3(side * 0.5, 1, -0.3));
  }
  // the stub wings: a pod outboard and a rail of four guided missiles inboard, a side; the team's band; the fin and the stabiliser
  for (const side of [-1, 1]) {
    top(body, side > 0 ? [0.45, 0.4, 2.35, 0.1, 2.35, -0.6, 0.45, -0.9] : [-0.45, 0.4, -2.35, 0.1, -2.35, -0.6, -0.45, -0.9], 0.12, p.dark, S + 1.0, 0.03);
    m.mounts.push({ gun: 'rockets', at: rocketPod(body, p, side * 2.0, S + 0.62, -0.25, 1.5, 0.28) });
    m.mounts.push({ gun: 'missiles', at: missileRail(body, p, side * 1.2, S + 0.72, -0.25) });
    slab(body, rect(-6.4, S + 0.95, -5.0, S + 1.15), 0.03, p.flash, side * 0.5);
    dispenser(m, body, side * 0.42, S + 1.15, -3.8, side);
  }
  slab(body, [-7.9, S + 1.45, -8.9, S + 3.1, -9.4, S + 3.1, -9.2, S + 1.2], 0.18, p.body, 0, 0.04);   // the fin
  top(body, [-1.3, -7.2, 1.3, -7.2, 1.3, -7.7, -1.3, -7.7], 0.1, p.dark, S + 1.25, 0.02);           // the stabiliser
  tailRotor(m, body, 0.3, S + 2.55, -9.15, 2, 1.25, p.dark);
  // the chin turret: a 20 mm with three barrels (it turns and its cradle pitches)
  const chin = group(body, 0, S - 0.12, 4.0); m.yaw.push(chin);
  top(chin, ring(0, 0, 0.34, 10, Math.PI / 10), 0.3, p.dark, -0.15, 0.05);
  const cradle = group(chin, 0, -0.18, 0.15); m.pitch.push(cradle);
  for (let i = 0; i < 3; i++) cyl(cradle, 0.035, 1.3, STEEL, Math.cos(i * 2.09) * 0.07, Math.sin(i * 2.09) * 0.07, 0.75, 'z', 6).userData.thin = true;
  m.mounts.push({ gun: 'cannon', at: chin });
  // the seats: the gunner low in front, the pilot above behind
  m.seats.push({ role: 'gunner', at: anchor(body, 0, S + 0.45, 3.0), gun: 'cannon' });
  m.seats.push({ role: 'pilot', at: anchor(body, 0, S + 0.95, 1.15) });
  // the skids
  for (const side of [-1, 1]) {
    for (const z of [2.2, -1.2]) { const st = box(body, 0.08, S + 0.1, 0.08, STEEL, side * 0.75, (S + 0.1) / 2, z); st.rotation.z = side * 0.3; }
    slab(body, [3.3, 0.0, 3.7, 0.35, 3.6, 0.4, 3.2, 0.1, -2.0, 0.1, -2.0, 0.0], 0.1, STEEL, side * 0.92, 0.02);
  }
  cyl(body, 0.12, 0.55, STEEL, 0, S + 2.45, -0.3, 'y', 8);   // the mast
  rotor(m, body, 0, S + 2.75, -0.3, 4, 6.3, p.dark, 24, 0.45);
  m.rotorR = 6.3;
  return m;
}

// ---------------------------------------------------------------------------
// C: the armed utility
// ---------------------------------------------------------------------------

// The hull's loft (73.1): stations along z, each a ten-sided section (an octagon, its sides split at the sill line)
interface Station { z: number; hw: number; yb: number; yt: number; c: number; yw?: number }
/** A section's faces in turn: the upper chamfer, the side over the sill, the side under it, the lower chamfer (a side each), the bottom, the top. */
type Face = 'chamfer' | 'upper' | 'lower' | 'belly' | 'bottom' | 'top';
const RING_FACES: Face[] = ['chamfer', 'upper', 'lower', 'belly', 'bottom', 'belly', 'lower', 'upper', 'chamfer', 'top'];
function ringOf(s: Station): Array<[number, number]> {
  const yw = Math.min(s.yt - s.c - 0.01, Math.max(s.yb + s.c + 0.01, s.yw ?? (s.yb + s.yt) / 2)), { hw, c, yb, yt } = s;
  return [[hw - c, yt], [hw, yt - c], [hw, yw], [hw, yb + c], [hw - c, yb], [-(hw - c), yb], [-hw, yb + c], [-hw, yw], [-hw, yt - c], [-(hw - c), yt]];
}
/**
 * A hull lofted through `st` (front to back), each face coloured by `paint(segment, face)`, a mesh a colour (the bake folds them
 * into one draw); an end capped in `cap`'s colour (null: open). Each face turned out from its section's middle.
 */
function loft(parent: THREE.Object3D, st: Station[], paint: (seg: number, face: Face) => string, cap: (end: 0 | 1) => string | null): void {
  const byColor = new Map<string, number[]>(), ab = new THREE.Vector3(), ac = new THREE.Vector3();
  const push = (color: string, a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, out: THREE.Vector3) => {
    const tri = ab.subVectors(b, a).cross(ac.subVectors(c, a)).dot(out) >= 0 ? [a, b, c] : [a, c, b];
    let arr = byColor.get(color); if (!arr) byColor.set(color, arr = []);
    for (const v of tri) arr.push(v.x, v.y, v.z);
  };
  const rings = st.map(s => ringOf(s).map(([x, y]) => new THREE.Vector3(x, y, s.z)));
  for (let i = 0; i + 1 < st.length; i++) {
    const A = rings[i], B = rings[i + 1], cy = (st[i].yb + st[i].yt + st[i + 1].yb + st[i + 1].yt) / 4;
    for (let e = 0; e < A.length; e++) {
      const e1 = (e + 1) % A.length;
      if (A[e].distanceTo(A[e1]) + B[e].distanceTo(B[e1]) < 1e-6) continue;
      const mid = A[e].clone().add(A[e1]).add(B[e]).add(B[e1]).multiplyScalar(0.25), out = new THREE.Vector3(mid.x, mid.y - cy, 0), color = paint(i, RING_FACES[e]);
      push(color, A[e], A[e1], B[e1], out); push(color, A[e], B[e1], B[e], out);
    }
  }
  for (const end of [0, 1] as const) {
    const color = cap(end); if (!color) continue;
    const R = rings[end ? rings.length - 1 : 0], s = st[end ? st.length - 1 : 0], c = new THREE.Vector3(0, (s.yb + s.yt) / 2, s.z), out = new THREE.Vector3(0, 0, end ? -1 : 1);
    for (let e = 0; e < R.length; e++) push(color, c, R[e], R[(e + 1) % R.length], out);
  }
  for (const [color, arr] of byColor) {
    const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3)); geo.computeVertexNormals();
    parent.add(new THREE.Mesh(geo, mat(color)));
  }
}
/** A rotor's blur disc (73.1, R1): one faint ring under the blades, turning with them (round); one draw a rotor once baked. */
function blurDisc(parent: THREE.Object3D, r: number, axis: 'y' | 'x', color: string): void {
  const d = new THREE.Mesh(new THREE.RingGeometry(r * 0.08, r, 32, 1), new THREE.MeshLambertMaterial({ color, transparent: true, opacity: 0.14, depthWrite: false, side: THREE.DoubleSide }));
  if (axis === 'y') { d.rotation.x = -Math.PI / 2; d.position.y = 0.04; } else d.rotation.y = Math.PI / 2;
  parent.add(d);
}
/** C's belly under the sill line (73.1, F1: counter-shaded — the team's colour above, this under). */
const BELLY = '#6c7176', TIP = '#d8b632';

/**
 * C in the owner's picks from 73.1's sheet (DECISIONS 329; design_sheets/helicopter_style.html: S2 D1 R1 F1): a lofted hull —
 * a nose cone, the windscreen behind it, the cabin, a tail cone from the cabin's back to a slim boom (where the sim's cabin
 * box ends, core/shipBody.ts HELI_BODY: drawn where it is hit) — one doghouse over both engines, sponsons over the main
 * wheels, a raked fin; a rotor head, yellow tips, a sensor ball, wire cutters, pitots, steps, antennae, the drive cover; a blur
 * disc under each rotor; the team's colour above the sill line and grey under it. Its seats, mounts, doors, gear and rotor are
 * the look's as before, and it keeps inside today's extremes (the wreck's hit shape is fitted from it: constants/hitboxes.ts
 * SHIP_WRECK_PIECES.heli).
 */
function armed(p: UnitPalette, personHeight: number): HeliModel {
  const m = blank('armed', personHeight), body = m.body;
  const S = 0.6, W = 2.4, hw = W / 2, YW = S + 1.3;   // (YW: the sill line — the cockpit's side windows' foot, the counter-shade's edge)
  const shade = (face: Face) => face === 'lower' || face === 'belly' || face === 'bottom' ? BELLY : p.body;
  // the hull: the nose and cockpit to the cabin's front bulkhead (glass: the windscreen, the cockpit's side windows)
  loft(body, [
    { z: 4.6, hw: 0.26, yb: S + 0.62, yt: S + 1.02, c: 0.1, yw: S + 0.82 },
    { z: 4.15, hw: 0.9, yb: S + 0.22, yt: S + 1.62, c: 0.3, yw: S + 1.05 },
    { z: 3.2, hw, yb: S, yt: S + 2.4, c: 0.3, yw: YW },
    { z: 1.0, hw, yb: S, yt: S + 2.45, c: 0.22, yw: YW },
    { z: 0.6, hw, yb: S, yt: S + 2.45, c: 0.22, yw: YW },
  ], (seg, face) => (seg === 1 && (face === 'chamfer' || face === 'upper' || face === 'top')) || (seg === 2 && face === 'upper') ? GLASS : shade(face), end => end ? p.body : BELLY);
  // the doorway's roof and floor (the doors open both sides)
  const roofAt = (z: number): Station => ({ z, hw, yb: S + 2.12, yt: S + 2.45, c: 0.22, yw: S + 2.3 });
  loft(body, [roofAt(0.6), roofAt(-1.9)], (_, face) => face === 'bottom' ? BELLY : p.body, () => null);
  const floorAt = (z: number): Station => ({ z, hw, yb: S, yt: S + 0.12, c: 0.05 });
  loft(body, [floorAt(0.6), floorAt(-1.9)], (_, face) => face === 'top' ? p.dark : BELLY, () => null);
  // the cabin's back, the tail cone (from z −2.2, where the sim's cabin ends), the boom to the fin
  loft(body, [
    { z: -1.9, hw, yb: S, yt: S + 2.45, c: 0.22, yw: YW },
    { z: -2.2, hw, yb: S, yt: S + 2.45, c: 0.22, yw: YW },
    { z: -3.3, hw: 0.62, yb: S + 1.25, yt: S + 2.4, c: 0.2, yw: S + 1.8 },
    { z: -9.6, hw: 0.3, yb: S + 1.85, yt: S + 2.25, c: 0.08, yw: S + 2.05 },
  ], (_, face) => shade(face), () => p.body);
  // the doghouse over both engines (no taller than they stood), its intakes; the boxed IR suppressors turned out (the plume mixed, aimed up and away)
  const dh = (z: number, w: number, h: number, c: number): Station => ({ z, hw: w, yb: S + 2.38, yt: S + 2.38 + h, c });
  loft(body, [dh(2.3, 0.45, 0.3, 0.14), dh(1.5, 0.95, 0.72, 0.3), dh(-2.5, 0.95, 0.72, 0.3), dh(-3.6, 0.55, 0.4, 0.18)], () => p.dark, () => null);
  for (const side of [-1, 1]) {
    slab(body, rect(1.1, S + 2.62, 1.45, S + 2.9), 0.02, RUBBER, side * 0.955);
    const sup = group(body, side * 0.85, S + 2.8, -3.0);
    box(sup, 0.5, 0.5, 1.1, STEEL, side * 0.15, 0, 0);
    for (let i = 0; i < 4; i++) box(sup, 0.02, 0.36, 0.06, RUBBER, side * 0.41, 0, -0.4 + i * 0.25);   // its louvres
    exhaust(m, body, side * 1.3, S + 2.85, -3.0, new THREE.Vector3(side * 1, 0.6, -0.3));
  }
  // the stub wings over the cabin (a pod a side); the sliding doors (team above the sill, grey under) and their .50s; the bands, the flares; the sponsons and wheels
  for (const side of [-1, 1]) {
    top(body, side > 0 ? [1.2, 0.3, 3.0, 0.1, 3.0, -0.7, 1.2, -1.0] : [-1.2, 0.3, -3.0, 0.1, -3.0, -0.7, -1.2, -1.0], 0.12, p.dark, S + 2.25, 0.03);
    m.mounts.push({ gun: 'rockets', at: rocketPod(body, p, side * 2.65, S + 1.85, -0.3, 1.5, 0.3) });
    // (73.2, the owner's nit: one door slid back 2.4 hung in the air beside the tail cone — the full-width cabin ends 0.3 behind
    // the doorway. Two leaves stack at the doorway's back instead: the rear one onto the cabin's back, the front one over it
    // on the outer track; the doorway's front 1.55, the gunner's, clear)
    for (const [z0, z1, out, slide, win] of [[-1.95, -0.65, 0.03, -0.25, -1.5], [-0.65, 0.65, 0.08, -1.55, -0.2]] as const) {
      const door = group(body, side * (W / 2 + out), 0, 0); m.slides.push({ group: door, home: 0, slide });
      slab(door, rect(z0, S + 0.08, z1, YW), 0.04, BELLY, 0, 0.012);
      slab(door, rect(z0, YW, z1, S + 2.15), 0.04, p.body, 0, 0.012, [rev(rect(win, YW + 0.06, win + 0.85, S + 1.85))]);
      slab(door, rect(win, YW + 0.06, win + 0.85, S + 1.85), 0.01, GLASS);
    }
    box(body, 0.04, 0.04, 4.6, STEEL, side * (W / 2 + 0.06), S + 2.18, -1.1).userData.thin = true;
    box(body, 0.04, 0.04, 4.6, STEEL, side * (W / 2 + 0.06), S + 0.06, -1.1).userData.thin = true;
    const gun = group(body, side * 1.12, S + 1.1, 0.25); gun.rotation.y = side * Math.PI / 2; m.yaw.push(gun);
    const hmg = buildPlayHmg(p); hmg.group.position.set(0, 0, 0.15); gun.add(hmg.group);
    const seat = anchor(body, side * 0.75, S + 0.16, 0.25); seat.name = `door gunner ${side < 0 ? 'left' : 'right'}`; seat.rotation.y = side * Math.PI / 2;
    m.seats.push({ role: 'gunner', at: seat, gun: 'hmg' });
    slab(body, rect(0.8, S + 0.35, 2.6, S + 0.55), 0.03, p.flash, side * (W / 2 + 0.01));
    // (the boom's band on the tail cone's side, which narrows 0.32 over its 6.3: turned to lie along it, 0.03 off it)
    const slope = 0.32 / 6.3, zc = -6.7, band = slab(body, rect(-0.9, S + 1.92, 0.9, S + 2.1), 0.02, p.flash, side * (0.62 + (zc + 3.3) * slope + 0.03));
    band.position.z = zc; band.rotation.y = side * Math.atan(slope);
    dispenser(m, body, side * 0.56, S + 1.55, -5.0, side);
    top(body, side > 0 ? [hw - 0.05, 2.75, hw + 0.42, 2.45, hw + 0.42, 1.25, hw - 0.05, 0.95] : [-(hw - 0.05), 2.75, -(hw + 0.42), 2.45, -(hw + 0.42), 1.25, -(hw - 0.05), 0.95], 0.55, BELLY, S + 0.12, 0.1);
    box(body, 0.12, 0.5, 0.12, STEEL, side * (hw + 0.3), S - 0.05, 1.9);
    cyl(body, 0.32, 0.22, RUBBER, side * (hw + 0.3), 0.32, 1.9, 'x', 12);
  }
  // the tail wheel on its strut (to the boom's foot), the raked fin, the stabiliser, the tail rotor
  const wheelY = 0.2 + S * 0.5, footY = S + 1.25 + (5.0 / 6.3) * 0.6 + 0.08;
  box(body, 0.1, footY - wheelY, 0.1, STEEL, 0, (footY + wheelY) / 2, -8.3); cyl(body, 0.2, 0.14, RUBBER, 0, wheelY, -8.3, 'x', 10);
  slab(body, [-8.2, S + 2.0, -9.6, S + 4.0, -10.3, S + 4.0, -10.1, S + 1.8], 0.2, p.body, 0, 0.05);
  top(body, [-1.5, -8.9, 1.5, -8.9, 1.5, -9.5, -1.5, -9.5], 0.1, p.dark, S + 2.0, 0.02);
  tailRotor(m, body, -0.32, S + 3.4, -10.05, 4, 1.5, p.dark);
  blurDisc(m.spin[m.spin.length - 1].group, 1.5, 'x', p.dark);
  // the cabin: two benches of four facing out; the pilots
  for (const side of [-1, 1]) for (const z of [-1.6, -1.0, -0.4, 0.2]) m.seats.push({ role: 'passenger', at: anchor(body, side * 0.35, S + 0.1, z) });
  for (const [role, x] of [['pilot', 0.5], ['copilot', -0.5]] as const) m.seats.push({ role, at: anchor(body, x, S + 0.4, 2.9) });
  // the detail: the sensor ball and its lens, the pitots, the wire cutters, the steps, the antennae, the drive cover
  const ball = new THREE.Mesh(new THREE.IcosahedronGeometry(0.27, 1), mat(p.dark)); ball.position.set(0, S + 0.12, 3.9); body.add(ball);
  box(body, 0.16, 0.12, 0.04, GLASS, 0, S + 0.12, 4.17);
  for (const side of [-1, 1]) cyl(body, 0.025, 0.6, STEEL, side * 0.35, S + 0.95, 4.55, 'z', 5).userData.thin = true;
  const cutter = (y0: number, y1: number, z0: number, z1: number) => { slab(body, [z0, y0, z1, y1, z1 - 0.12, y1, z0 - 0.12, y0], 0.05, STEEL, 0).userData.thin = true; };
  cutter(S + 2.38, S + 2.85, 3.0, 2.55); cutter(S + 0.1, S - 0.35, 3.75, 3.4);
  for (const side of [-1, 1]) { box(body, 0.3, 0.04, 0.5, STEEL, side * (hw + 0.12), S + 0.32, 1.6); box(body, 0.3, 0.04, 0.7, STEEL, side * (hw + 0.12), S - 0.02, -0.65); }
  box(body, 0.04, 0.5, 0.25, STEEL, 0, S + 2.36 + 0.25, -4.9); box(body, 0.04, 0.4, 0.22, STEEL, 0, S - 0.2, -1.2);
  box(body, 0.3, 0.14, 3.6, p.dark, 0, S + 2.32, -7.3);
  // the rotor: the mast, the swashplate, the head (hub, grips), the blades' yellow tips, the disc
  cyl(body, 0.14, 0.7, STEEL, 0, S + 3.35, -0.5, 'y', 8);
  cyl(body, 0.36, 0.06, STEEL, 0, S + 3.42, -0.5, 'y', 10);
  const head = rotor(m, body, 0, S + 3.7, -0.5, 4, 7.6, p.dark, 22, 0.55);
  cyl(head, 0.42, 0.16, STEEL, 0, 0.12, 0, 'y', 10);
  for (let i = 0; i < 4; i++) { const g = group(head, 0, 0.12, 0); g.rotation.y = (i * Math.PI) / 2; box(g, 0.2, 0.14, 0.18, STEEL, 0.55, 0, 0); slab(g, [7.0, -0.13, 7.62, -0.14, 7.62, 0.02, 7.0, 0.05], 0.58, TIP, 0, 0); }   // (the tip a sleeve over the blade's end: no shared face)
  blurDisc(head, 7.6, 'y', p.dark);
  m.rotorR = 7.6;
  return m;
}

// ---------------------------------------------------------------------------
// D: the compound
// ---------------------------------------------------------------------------

function compound(p: UnitPalette, personHeight: number): HeliModel {
  const m = blank('compound', personHeight), body = m.body;
  const S = 0.65, W = 1.9;
  // a sleek fuselage: a long sloped nose, the cabin with one door (the right), the tail tapering to the pusher's hub
  slab(body, [5.0, S + 0.85, 4.5, S + 0.2, 3.0, S, -2.0, S, -4.5, S + 0.9, -7.4, S + 1.25, -7.6, S + 1.55, -7.4, S + 1.8, -4.2, S + 2.15, -1.8, S + 2.3, 2.0, S + 2.3, 3.6, S + 1.85], W, p.body, 0, 0.2,
    [rev(rect(-1.6, S + 0.12, 0.5, S + 2.0))]).name = 'body';
  top(body, rect(-0.93, -1.6, 0.93, 0.5), 0.04, p.dark, S + 0.1, 0.01);
  slab(body, [3.9, S + 1.85, 2.35, S + 2.28, 2.1, S + 1.4, 3.85, S + 0.98, 4.6, S + 1.0], W + 0.03, GLASS);
  // the left side whole (its door is on the right): a panel over the cut there
  slab(body, rect(-1.6, S + 0.12, 0.5, S + 2.0), 0.04, p.body, -(W / 2 + 0.01), 0.01);
  const door = group(body, W / 2 + 0.03, 0, 0); m.slides.push({ group: door, home: 0, slide: -2.1 });
  slab(door, rect(-1.65, S + 0.08, 0.55, S + 2.05), 0.04, p.body, 0, 0.012, [rev(rect(-1.2, S + 1.25, -0.2, S + 1.75))]);
  slab(door, rect(-1.2, S + 1.25, -0.2, S + 1.75), 0.01, GLASS);
  box(body, 0.04, 0.04, 3.9, STEEL, W / 2 + 0.06, S + 2.08, -1.1).userData.thin = true;
  // the engine and the coaxial mast's fairing; the exhausts aft, mixed into the tail's flow
  slab(body, [1.6, S + 2.3, 1.2, S + 2.85, -2.6, S + 2.9, -3.4, S + 2.3], 1.2, p.dark, 0, 0.14);
  for (const side of [-1, 1]) exhaust(m, body, side * 0.62, S + 2.5, -3.3, new THREE.Vector3(side * 0.2, 0.1, -1));
  cyl(body, 0.32, 1.25, p.dark, 0, S + 3.35, -0.4, 'y', 10);   // the mast's fairing between the rotors
  // the pusher: a shrouded hub, a five-blade propeller turning about z; the tail's fins, an H
  cyl(body, 0.3, 0.6, p.dark, 0, S + 1.5, -7.8, 'z', 10);
  const prop = group(body, 0, S + 1.5, -8.15); m.spin.push({ group: prop, axis: 'z', rate: 40 });
  for (let i = 0; i < 5; i++) { const b = box(prop, 0.16, 1.5, 0.04, p.dark, 0, 0, 0); b.rotation.z = (i * 2 * Math.PI) / 5; b.geometry.translate(0, 0.75, 0); }
  top(body, [-1.6, -6.5, 1.6, -6.5, 1.6, -7.1, -1.6, -7.1], 0.1, p.dark, S + 1.5, 0.02);
  for (const side of [-1, 1]) slab(body, [-6.4, S + 1.0, -6.9, S + 2.35, -7.25, S + 2.35, -7.1, S + 1.0], 0.12, p.body, side * 1.6, 0.03);
  // the fold-out arms with a pod each; the band; the flares; the retracting wheels (down)
  for (const side of [-1, 1]) {
    box(body, 1.1, 0.12, 0.6, p.dark, side * (W / 2 + 0.5), S + 1.15, -0.4);
    m.mounts.push({ gun: 'rockets', at: rocketPod(body, p, side * (W / 2 + 0.95), S + 0.85, -0.4, 1.3, 0.26) });
    slab(body, rect(1.0, S + 0.4, 2.8, S + 0.58), 0.03, p.flash, side * (W / 2 + 0.01));
    dispenser(m, body, side * 0.5, S + 1.0, -4.8, side);
    cyl(body, 0.28, 0.2, RUBBER, side * 0.85, 0.28, 2.3, 'x', 12); box(body, 0.1, S, 0.1, STEEL, side * 0.85, S / 2 + 0.2, 2.3);
    cyl(body, 0.28, 0.2, RUBBER, side * 0.85, 0.28, -2.4, 'x', 12); box(body, 0.1, S, 0.1, STEEL, side * 0.85, S / 2 + 0.2, -2.4);
  }
  // the door gun (the right), the chin gun
  const gun = group(body, 0.85, S + 1.1, -0.2); gun.rotation.y = Math.PI / 2; m.yaw.push(gun);
  const hmg = buildPlayHmg(p); hmg.group.position.set(0, 0, 0.15); gun.add(hmg.group);
  const seat = anchor(body, 0.5, S + 0.16, -0.2); seat.name = 'door gunner right'; seat.rotation.y = Math.PI / 2;
  m.seats.push({ role: 'gunner', at: seat, gun: 'hmg' });
  const chin = group(body, 0, S - 0.05, 3.6); m.yaw.push(chin);
  top(chin, ring(0, 0, 0.28, 8, Math.PI / 8), 0.26, p.dark, -0.13, 0.05);
  const cradle = group(chin, 0, -0.08, 0.1); m.pitch.push(cradle);
  cyl(cradle, 0.05, 1.1, STEEL, 0, 0, 0.6, 'z', 6).userData.thin = true;
  m.mounts.push({ gun: 'cannon', at: chin });
  for (const z of [-1.4, -0.85, -0.3]) for (const x of [-0.4, 0.2]) m.seats.push({ role: 'passenger', at: anchor(body, x, S + 0.1, z) });
  for (const [role, x] of [['pilot', 0.42], ['copilot', -0.42]] as const) m.seats.push({ role, at: anchor(body, x, S + 0.4, 2.6) });
  // the coaxial rotors: two four-blade rotors turning opposite ways, stacked
  rotor(m, body, 0, S + 3.0, -0.4, 4, 5.8, p.dark, 24, 0.42);
  rotor(m, body, 0, S + 3.75, -0.4, 4, 5.8, p.dark, -24, 0.42);
  cyl(body, 0.25, 0.25, p.metal, 0, S + 4.0, -0.4, 'y', 8);
  m.rotorR = 5.8;
  return m;
}

/** Build a look `personHeight` world units per person. */
export function buildHelicopter(look: HeliLook, p: UnitPalette, personHeight: number): HeliModel {
  return look === 'utility' ? utility(p, personHeight) : look === 'attack' ? attack(p, personHeight) : look === 'armed' ? armed(p, personHeight) : compound(p, personHeight);
}

/** The sheet's motion: `aim` / `aimPitch` the guns, `doors` 0 shut … 1 open, `thrust` 0 … 1 (the rotors' spin), `bank` / `pitch` the body's (radians); `dt` spins. */
export function updateHelicopter(m: HeliModel, s: { aim: number; aimPitch?: number; doors: number; thrust: number; bank?: number; pitch?: number }, dt: number): void {
  for (const y of m.yaw) y.rotation.y = (y.userData.base ??= y.rotation.y) + s.aim;
  for (const q of m.pitch) q.rotation.x = -(s.aimPitch ?? 0.2);
  for (const sp of m.spin) sp.group.rotation[sp.axis] += sp.rate * dt * (0.35 + 0.65 * s.thrust);
  for (const d of m.slides) d.group.position.z = d.home + d.slide * s.doors;
  m.body.rotation.set(s.pitch ?? 0, 0, s.bank ?? 0);
}

/** Fold the static parts per moving group. */
export function bakeHelicopter(m: HeliModel): void {
  const moving: THREE.Object3D[] = [...m.yaw, ...m.pitch, ...m.spin.map(s => s.group), ...m.slides.map(d => d.group)];
  const under = (o: THREE.Object3D, r: THREE.Object3D) => { for (let q = o.parent; q; q = q.parent) if (q === r) return true; return false; };
  for (const g of [m.body, ...moving]) bakeStaticParts(g, { exclude: [m.body, ...moving].filter(o => o !== g && under(o, g)) });
}

/** Its triangles and draws. */
export function helicopterCost(m: HeliModel): { triangles: number; draws: number } {
  let triangles = 0, draws = 0;
  m.root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (let q: THREE.Object3D | null = o; q; q = q.parent) if (!q.visible) return;
    draws++; triangles += mesh.geometry.index ? mesh.geometry.index.count / 3 : mesh.geometry.attributes.position.count / 3;
  });
  return { triangles, draws };
}

/** Its seats and mounts (person heights, its frame on its gear), as aircraftData gives them. */
export function heliData(m: HeliModel): { seats: Array<{ role: AircraftRole; at: [number, number, number]; gun?: AircraftGun }>; mounts: Array<{ gun: AircraftGun; at: [number, number, number] }> } {
  m.root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(m.root.matrixWorld).invert(), k = m.root.scale.x / 1.8;
  const at = (o: THREE.Object3D): [number, number, number] => { const v = o.getWorldPosition(new THREE.Vector3()).applyMatrix4(inv).multiplyScalar(k); return [Math.round(v.x * 100) / 100, Math.round(v.y * 100) / 100, Math.round(v.z * 100) / 100]; };
  return { seats: m.seats.map(s => ({ role: s.role, at: at(s.at), ...(s.gun ? { gun: s.gun } : {}) })), mounts: m.mounts.map(q => ({ gun: q.gun, at: at(q.at) })) };
}
